import { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  RuleCategoryEnum,
  RuleNameEnum,
  RuleSeverityEnum,
} from '@/__generated__/graphql';
import { RecommendationsList } from '@/components/Recommendations/RecommendationsList';
import {
  RecommendationTableFilter,
  SelectedFilters,
} from '@/components/Recommendations/RecommendationTableFilter';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useRecommendations } from '@/hooks/discovery/recommendations';
import { useRecommendationsCount } from '@/hooks/discovery/recommendationsCount';
import { truthy } from '@/util/array';

interface EvaluatorViewProps {
  uniqueId?: string;
}

export const EvaluatorView: FC<EvaluatorViewProps> = ({ uniqueId }) => {
  const { environmentId } = useDbtCloudProject();

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>();

  useEffect(() => {
    setSelectedFilters({
      category: [searchParams.get('category')].filter(truthy) as RuleCategoryEnum[],
      ruleName: [searchParams.get('ruleName')].filter(truthy) as RuleNameEnum[],
      severity: [searchParams.get('severity')].filter(truthy) as RuleSeverityEnum[],
    });
  }, [searchParams]);

  const [recommendationCount] = useRecommendationsCount({
    environmentId,
    uniqueId,
  });

  const unfilteredCount = recommendationCount.data?.recommendations.rules?.totalCount;

  const results = useRecommendations({
    environmentId: environmentId || 0,
    first: 300,
    filter: {
      ruleNames: selectedFilters?.ruleName.length ? selectedFilters.ruleName : null,
      severity: selectedFilters?.severity.length ? selectedFilters.severity : null,
      categories: selectedFilters?.category.length ? selectedFilters.category : null,
      uniqueIds: [uniqueId || ''],
    },
    pause: !environmentId || !uniqueId || unfilteredCount === 0,
  });

  const getRecommendationsTitle = (count: number | undefined) => {
    if (count === undefined) return '';
    if (count === 1) return '1 Recommendation';
    return `${count} Recommendations`;
  };

  const header = getRecommendationsTitle(
    results.pageInfo?.totalCount ?? unfilteredCount ?? undefined,
  );

  return (
    <div className="mt-4">
      <RecommendationTableFilter
        className="mb-4"
        header={header}
        selectedFilters={selectedFilters}
        isDisabled={unfilteredCount === 0}
        onChange={(filters) => {
          setSearchParams((searchParams) => {
            if (filters.category.length === 0) {
              searchParams.delete('category');
            } else {
              searchParams.set('category', filters.category.join(','));
            }
            if (filters.severity.length === 0) {
              searchParams.delete('severity');
            } else {
              searchParams.set('severity', filters.severity.join(','));
            }
            if (filters.ruleName.length === 0) {
              searchParams.delete('ruleName');
            } else {
              searchParams.set('ruleName', filters.ruleName.join(','));
            }
            return searchParams;
          });
        }}
      />
      <RecommendationsList
        recommendations={results.data || []}
        isLoading={results.fetching}
      />
    </div>
  );
};
